/**
 * Solid brand color for the cash-tutorial artwork and its step indicator.
 *
 * Those marks sit on a white page. `primaryGradient` is often the lighter
 * stop of the partner mesh, and on this surface it disappears. When that
 * stop fails contrast, use the darker stop. If every stop is still too light
 * — the lime-to-yellow mesh is — darken the darkest stop just enough to clear
 * the floor, keeping the brand hue.
 *
 * 4.5:1 is the floor (WCAG AA for small marks). The drawings also tint their
 * mid tones by mixing this ink with the page, so a 3:1 ink still washes out.
 */

const MIN_CONTRAST = 4.5;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface BrandMeshColors {
  primaryGradient?: string;
  secondaryGradient?: string;
  primaryMesh?: string;
  gradient?: string;
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

const channel = (value: number) => {
  const s = value / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

const luminance = (color: Rgb) =>
  0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);

const contrast = (a: Rgb, b: Rgb) => {
  const l1 = luminance(a);
  const l2 = luminance(b);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
};

const toHex = (color: Rgb) => {
  const byte = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, "0");
  return `#${byte(color.r)}${byte(color.g)}${byte(color.b)}`;
};

const mix = (from: Rgb, to: Rgb, amount: number): Rgb => ({
  r: from.r + (to.r - from.r) * amount,
  g: from.g + (to.g - from.g) * amount,
  b: from.b + (to.b - from.b) * amount,
});

const parseColor = (input: string | undefined | null): Rgb | null => {
  if (!input) return null;
  const value = input.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value);
  if (hex) {
    let digits = hex[1];
    if (digits.length === 3) {
      digits = digits
        .split("")
        .map((char) => char + char)
        .join("");
    }
    if (digits.length === 8) digits = digits.slice(0, 6);
    return {
      r: Number.parseInt(digits.slice(0, 2), 16),
      g: Number.parseInt(digits.slice(2, 4), 16),
      b: Number.parseInt(digits.slice(4, 6), 16),
    };
  }
  const rgb =
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i.exec(value);
  if (!rgb) return null;
  return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]) };
};

/** Solid stops, plus any hex / rgb() stops embedded in a gradient string. */
const collectStops = (...sources: Array<string | undefined>): Rgb[] => {
  const stops: Rgb[] = [];
  const seen = new Set<string>();
  const push = (color: Rgb | null) => {
    if (!color) return;
    const key = toHex(color);
    if (seen.has(key)) return;
    seen.add(key);
    stops.push(color);
  };

  for (const source of sources) {
    if (!source) continue;
    push(parseColor(source));
    for (const match of source.matchAll(
      /#(?:[0-9a-f]{8}|[0-9a-f]{6}|[0-9a-f]{3})\b/gi,
    )) {
      push(parseColor(match[0]));
    }
    for (const match of source.matchAll(/rgba?\([^)]+\)/gi)) {
      push(parseColor(match[0]));
    }
  }
  return stops;
};

/** Move `color` toward black or white until it clears the page. */
const ensureContrast = (color: Rgb, background: Rgb): string => {
  if (contrast(color, background) >= MIN_CONTRAST) return toHex(color);
  const toward = luminance(background) > 0.5 ? BLACK : WHITE;
  let lo = 0;
  let hi = 1;
  let ink = mix(color, toward, 1);
  for (let step = 0; step < 16; step += 1) {
    const amount = (lo + hi) / 2;
    const mixed = mix(color, toward, amount);
    if (contrast(mixed, background) >= MIN_CONTRAST) {
      ink = mixed;
      hi = amount;
    } else {
      lo = amount;
    }
  }
  return toHex(ink);
};

export const accentInkOnSurface = (
  colors: BrandMeshColors | null | undefined,
  background: string | undefined,
  fallback: string,
): string => {
  const page = parseColor(background) ?? WHITE;
  const primary = parseColor(colors?.primaryGradient);
  if (primary && contrast(primary, page) >= MIN_CONTRAST) return toHex(primary);

  const stops = collectStops(
    colors?.primaryGradient,
    colors?.secondaryGradient,
    colors?.primaryMesh,
    colors?.gradient,
  );
  const pool = stops.length > 0 ? stops : [parseColor(fallback) ?? BLACK];
  let best = pool[0];
  let bestContrast = contrast(best, page);
  for (const stop of pool) {
    const ratio = contrast(stop, page);
    if (ratio > bestContrast) {
      best = stop;
      bestContrast = ratio;
    }
  }
  return ensureContrast(best, page);
};
